import { Box, Button, Divider, Grid2, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react';
import FunctionalLocationInfo from '../components/Alerts/FunctionalLocationInfo';
import FuncLocationTopLeftBackgroundHigh from '../assets/alert-tile-bg/func-loc-top-left-high.svg';
import FuncLocationTopLeftBackgroundHighMed from '../assets/alert-tile-bg/func-loc-top-left-highmedium.svg';
import FuncLocationTopLeftBackgroundMedium from '../assets/alert-tile-bg/func-loc-top-left-medium.svg';
import FuncLocationTopLeftBackgroundLow from '../assets/alert-tile-bg/func-loc-top-left-low.svg';
import FuncLocationTopRightBackground from '../assets/alert-tile-bg/func-loc-top-right.svg';
import CheckIcon from '@mui/icons-material/Check';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getFuncLocInfo, selectIfFuncLocInfo, selectIfFuncLocInfoLoading } from '../store/slices/emergingAlertsSlice';
import { AppDispatch } from '../store/store';
import { GET_ESCALATION_COLOR } from '../constants/SeverityColors';
import DarkAdnocLoadingProgress from '../components/shared/DarkAdnocLoadingProgress';
import { MESSAGES } from '../constants/Messages';
import { convertToPixelFromVH, convertToPixelFromVW } from '../utils/ViewportUtils';
import { APP_ROUTES } from '../constants/AppRoutes';
import React from 'react';

const PRIORITY_SORT_ORDER = {
    'High': 4,
    'High-Medium': 3,
    'Medium': 2,
    'Low': 1,
}

const SORT_FN = (first: any, second: any) => {
    if (typeof first === 'string') {
        return PRIORITY_SORT_ORDER[second as keyof typeof PRIORITY_SORT_ORDER]
            - PRIORITY_SORT_ORDER[first as keyof typeof PRIORITY_SORT_ORDER]
    }
    return PRIORITY_SORT_ORDER[second.escalation_rating as keyof typeof PRIORITY_SORT_ORDER]
        - PRIORITY_SORT_ORDER[first.escalation_rating as keyof typeof PRIORITY_SORT_ORDER]
}

export default function DeivationRadarAreasView() {

    const dispatch = useDispatch<AppDispatch>();

    const { area, areaCode } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const areaDescription = queryParams.get('areaDescription');
    const areaPriority = queryParams.get('areaPriority');

    const isFuncLocInfoLoading = useSelector(selectIfFuncLocInfoLoading);
    const flData = useSelector(selectIfFuncLocInfo(areaCode ?? ''), shallowEqual);

    const [priorityFilter, setPriorityFilter] = useState<string[]>([...Object.keys(PRIORITY_SORT_ORDER)]);

    const groupByPriority = useMemo(() => {
        return flData.reduce((acc: any, item: any) => {
            const groupKey = item['escalation_rating'];
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(item);
            return acc;
        }, {});
    }, [flData])

    const filteredData = useMemo(() => {
        const filtered = flData
            .filter((s: any) =>
                s.area_code === areaCode &&
                (priorityFilter.includes(s.escalation_rating) || priorityFilter.length === 0));
        const sorted = filtered.sort(SORT_FN)
        return sorted;

    }, [flData, areaCode, priorityFilter])

    const handleApplySelectedPriority = (priority: string) => {
        if (priorityFilter.includes(priority))
            setPriorityFilter(prev => prev.filter(s => s !== priority));
        else
            setPriorityFilter(prev => [...prev, priority])
    }

    const getTopRightBackgroundIndicator = () => {
        if (!areaPriority) return FuncLocationTopLeftBackgroundLow;
        if (areaPriority!.toLowerCase() === 'high')
            return FuncLocationTopLeftBackgroundHigh;
        if (areaPriority!.toLowerCase() === 'high-medium')
            return FuncLocationTopLeftBackgroundHighMed;
        if (areaPriority!.toLowerCase() === 'medium')
            return FuncLocationTopLeftBackgroundMedium;
        return FuncLocationTopLeftBackgroundLow;
    }

    const showAllEscalationPriorities = () => {
        setPriorityFilter([...Object.keys(PRIORITY_SORT_ORDER)])
    }

    useEffect(() => {
        if (isFuncLocInfoLoading === null)
            dispatch(getFuncLocInfo())
    }, [dispatch, isFuncLocInfoLoading])

    return (
        <Grid2 sx={{
            width: '100vw',
            height: '100vh',
            position: 'relative',
            top: 'max(64px, 6vh)',
            paddingX: 2,
            paddingTop: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}>
            <Box id='functional-location' sx={{
                width: '75%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                borderLeft: `0.5vw solid ${GET_ESCALATION_COLOR(areaPriority!)}`,
                boxSizing: 'border-box',
            }}>
                <Box sx={{
                    paddingX: 1,
                    boxSizing: 'border-box',
                    background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) -7.34%, rgba(255, 255, 255, 0.52) 50%)',
                }}>
                    <Typography variant='h5' fontWeight={500}>
                        {areaDescription}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight={500}>
                        {areaCode}
                    </Typography>
                </Box>
                <Box sx={{
                    width: '20vw',
                    height: '6vh',
                    display: 'grid',
                    alignItems: 'center',
                    justifyItems: 'flex-end',
                    paddingY: 2,
                    paddingX: 3,
                    boxSizing: 'border-box',
                    background: `linear-gradient(to left, ${GET_ESCALATION_COLOR(areaPriority!)}6B 42%, ${GET_ESCALATION_COLOR(areaPriority!)}00 75%)`,
                    clipPath: `path('M 0 0 H ${convertToPixelFromVW(20)} V ${convertToPixelFromVH(4)} L ${convertToPixelFromVW(17.5)} ${convertToPixelFromVH(7.5)} H 0 Z')`,
                }}>
                    <Typography variant='subtitle1'
                        fontWeight={500}
                        fontStyle={'italic'}>
                        Area Escalation Priority : <strong>{areaPriority}</strong>
                    </Typography>
                </Box>
            </Box>
            <Box id='site-path-and-filters' sx={{
                width: '100%',
                display: 'flex',
            }}>
                <Box className="alerts_site_path" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}>
                    <NavLink to={APP_ROUTES.DEVIATION_RADAR} className='masthead_link' style={{ color: '#00A9B1' }} >
                        Deviation Radar
                    </NavLink>
                    <NavLink to={`${APP_ROUTES.DEVIATION_RADAR}/${area}`} className='masthead_link' style={{ color: '#00A9B1' }} >
                        {area}
                    </NavLink>
                    {/* <Button variant='text'
                        startIcon={<KeyboardArrowLeftIcon />}
                        sx={{
                            
                            
                            fontWeight: 600,
                            textDecoration: 'underline',
                            color: '#00A9B1'
                        }}>
                        Deviation Radar
                    </Button>
                    <Button variant='text'
                        startIcon={<KeyboardArrowLeftIcon />}
                        sx={{
                            
                            
                            fontWeight: 600,
                            textDecoration: 'underline',
                            color: '#00A9B1'
                        }}>
                        {area}
                    </Button> */}
                </Box>
                <Box sx={{
                    marginLeft: 2,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: 1,
                }}>
                    <Button
                        onClick={showAllEscalationPriorities}
                        sx={{
                            fontWeight: 700,
                            paddingX: '1vw',
                            borderRadius: '1vw',
                            border: '2px solid #00A9B1',
                            boxShadow: '0px 0px 9px 0px #12AEB5DB inset'
                        }}>
                        Show Alert Escalation Priorities
                    </Button>
                    {Object.keys(groupByPriority).sort(SORT_FN).map((key) => {
                        return <Button
                            key={key}
                            onClick={() => handleApplySelectedPriority(key)}
                            startIcon={(priorityFilter.includes(key) ? <CheckIcon /> : <></>)}
                            className={(priorityFilter.includes(key) ? 'selected' : '')}
                            sx={{
                                fontWeight: 700,
                                paddingX: '1vw',
                                paddingY: '0.25vh',
                                borderRadius: '1vw',
                                border: '2px solid #00A9B1',
                                boxShadow: '0px 0px 7px 0px #12AEB5DB inset',
                                '&.selected': {
                                    boxShadow: '0px 0px 25px 0px #12AEB5DB inset',
                                }
                            }}>
                            {`${key}(${groupByPriority[key].length})`}
                        </Button>
                    })}
                </Box>
            </Box>

            <Grid2 id='events'
                size={{ lg: 8 }}
                sx={{
                    // width: { xs: '100%', lg: 'calc(100% - 550px)' },
                    padding: 5,
                    background: `url('${getTopRightBackgroundIndicator()}') no-repeat, url('${FuncLocationTopRightBackground}') no-repeat`,
                    backgroundPosition: '1% 0, 100% 5px',
                    backgroundSize: '5vw, 5vh'
                }}>
                <Grid2 sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '.1fr 1fr 1.5fr 1fr 1fr 1fr 1fr',
                    columnGap: 2,
                    '& > *': {
                        fontWeight: 500
                    },
                }}>
                    <Box></Box>
                    <Box>Location Name</Box>
                    <Box>Location Description</Box>
                    <Box>Barrier Type</Box>
                    <Box>Barrier Description</Box>
                    <Box>Escalation Priority</Box>
                    <Box>Timeline</Box>
                </Grid2>
                <Divider sx={{ height: '1px', width: '100%', marginY: 2 }}></Divider>
                {
                    isFuncLocInfoLoading === true &&
                    <Box sx={{
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <Typography variant='body1'>
                            {MESSAGES.DATA_LOADING_PLACEHOLDER}
                        </Typography>
                        <DarkAdnocLoadingProgress size='xlarge'></DarkAdnocLoadingProgress>
                    </Box>
                }
                {
                    isFuncLocInfoLoading === false && flData && flData.length === 0 &&
                    <Box sx={{
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <Typography>No data found.</Typography>
                    </Box>
                }
                {
                    flData && flData.length > 0 && filteredData.map((fl: any, index: number) => {
                        return <React.Fragment key={index}>
                            <FunctionalLocationInfo flRow={fl}></FunctionalLocationInfo>
                        </React.Fragment>
                    })
                }
            </Grid2>
        </Grid2>
    )
}